import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BookOpen, Play, ArrowRight } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [cards, setCards] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const [c, cs] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/courses/${id}/cards`),
      ]);
      setCourse(c.data);
      setCards(cs.data);
    })();
  }, [id]);

  const startLearning = async () => {
    if (!user) {
      nav("/login", { state: { from: `/courses/${id}` } });
      return;
    }
    setEnrolling(true);
    try {
      await api.post(`/courses/${id}/enroll`);
      toast.success("Enrolled! Let's go 🚀");
      nav(`/learn/${id}`);
    } catch {
      toast.error("Could not enroll");
    } finally {
      setEnrolling(false);
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen grain-bg">
        <Nav />
        <div className="max-w-4xl mx-auto p-8">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg" data-testid="course-detail-page">
      <Nav />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="brutal-card overflow-hidden mb-8">
          <div className="relative h-56 border-b-2 border-brand-ink" style={{ backgroundColor: course.color }}>
            {course.image && <img src={course.image} alt={course.title} className="w-full h-full object-cover opacity-90" />}
            <div className="absolute top-5 left-5 w-20 h-20 rounded-2xl bg-white border-2 border-brand-ink shadow-brutal flex items-center justify-center text-5xl">
              {course.flag}
            </div>
            <div className="absolute bottom-5 right-5 brutal-badge bg-white">{course.level}</div>
          </div>
          <div className="p-8">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">{course.title}</h1>
            <p className="text-brand-navy/70 mt-3 text-lg">{course.description}</p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                onClick={startLearning}
                disabled={enrolling}
                data-testid="course-start-btn"
                className="brutal-btn-pink text-lg"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> Start learning <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </button>
              <div className="text-sm font-bold text-brand-navy/70 flex items-center gap-2">
                <BookOpen className="w-5 h-5" strokeWidth={3} /> {cards.length} cards · {course.language}
              </div>
            </div>
          </div>
        </div>

        <h2 className="font-display text-2xl font-bold text-brand-navy mb-4">What you'll learn</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {cards.slice(0, 12).map((card) => (
            <div key={card.id} className="brutal-card-sm p-4 bg-white">
              <div className="text-xs font-bold uppercase text-brand-navy/50">{course.language}</div>
              <div className="font-display font-bold text-brand-navy">{card.front}</div>
              <div className="text-xs font-bold uppercase text-brand-navy/50 mt-2">English</div>
              <div className="font-display font-bold text-brand-pink">{card.back}</div>
            </div>
          ))}
        </div>
        {cards.length > 12 && (
          <p className="text-center text-brand-navy/60 mt-6 font-bold">
            + {cards.length - 12} more waiting for you inside
          </p>
        )}
      </div>
    </div>
  );
}
